import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { intervalLabel, termMonths, type AgreementBilling, type RepeatIntervalUnit } from '@fieldops/shared'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine, money } from '../../lib/format.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { PriceInput } from '../proposal/PricingInspector.js'
import { dayLabel, leadLabel } from '../repeat/rule.js'
import { ServiceTracks, type TrackGroup } from '../repeat/ServiceTracks.js'
import { NewSitePanel } from '../SitePicker.js'
import { Button, ErrorState, Field, Input, Select, Spinner, SwitchTrack } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'
import {
  BILLING_LABEL,
  DEFAULT_FREQUENCY,
  FREQUENCIES,
  billingLine,
  builderTotals,
  draftSlots,
  firstDueOf,
  firstOfMonth,
  newDraftService,
  servicesFromJob,
  shortFrequency,
  yearFrom,
  type BuilderJob,
  type BuilderSettings,
  type DraftService,
  type Term,
} from './builder-model.js'

/**
 * How the builder was opened. From a client's page the client is chosen; from
 * a job the client, the site and the job's services are, and the job becomes
 * the first visit of its service. Extending adds sites and services to an
 * agreement that already exists, so it has no client-and-term step.
 */
/** What Open agreement tells the page it lands on, for its banner. */
export type AgreementCreated = { made: number; autoCreate: boolean; leadDays: number }

export type BuilderMode =
  | { kind: 'new'; client?: { id: string; name: string } | null; job?: BuilderJob | null }
  | { kind: 'extend'; agreementId: string; siteIds?: readonly string[] }

type Site = RouterOutputs['clients']['sitesForClient'][number]
type ServiceType = RouterOutputs['forms']['serviceTypes'][number]
type BuildResult = RouterOutputs['agreements']['build']
type Step = 'client' | 'sites' | 'services' | 'review'

const STEP_LABEL: Record<Step, string> = {
  client: 'Client and term',
  sites: 'Sites',
  services: 'Services',
  review: 'Review',
}

const BLOCKER: Record<Step, string> = {
  client: 'Pick a client and name the agreement',
  sites: 'Pick at least one site',
  services: 'Every site needs at least one service',
  review: '',
}

/**
 * The service agreement builder. Decision 96.
 *
 * Four steps — client and term, sites, services at each site, review — with a
 * running summary beside them. Every service at every site is its own repeat
 * schedule, written in one go by `agreements.build`. The dots are previewed
 * with the shared `repeatSlots`; once saved, the server's are the truth.
 *
 * Billing and renewal are recorded on the agreement and nothing acts on them
 * yet. Jobs are made straight away only while automatic creation is on.
 */
export function AgreementBuilder({
  open,
  mode,
  onClose,
}: {
  open: boolean
  mode: BuilderMode
  onClose: () => void
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="agreement-builder-title"
      className="h-[calc(100dvh-2rem)] max-w-[1280px]"
    >
      {open ? <BuilderLoader mode={mode} onClose={onClose} /> : null}
    </Modal>
  )
}

function BuilderLoader({ mode, onClose }: { mode: BuilderMode; onClose: () => void }) {
  const trpc = useTRPC()
  const context = useQuery(trpc.agreements.builderContext.queryOptions())
  const catalogue = useQuery(trpc.forms.serviceTypes.queryOptions())
  const agreement = useQuery({
    ...trpc.agreements.byId.queryOptions({ id: mode.kind === 'extend' ? mode.agreementId : '' }),
    enabled: mode.kind === 'extend',
  })

  const error = context.error ?? catalogue.error ?? (mode.kind === 'extend' ? agreement.error : null)
  const ready = context.data && catalogue.data && (mode.kind === 'new' || agreement.data)

  if (error || !ready) {
    return (
      <>
        <BuilderHeader title={mode.kind === 'extend' ? 'Add to the agreement' : 'New service agreement'} sub="" onClose={onClose} />
        <div className="grid flex-1 place-items-center p-8">
          {error ? <ErrorState message={errorMessage(error)} /> : <Spinner className="size-6 text-ink-400" />}
        </div>
      </>
    )
  }

  return (
    <Builder
      mode={mode}
      settings={context.data!}
      catalogue={catalogue.data!}
      agreement={mode.kind === 'extend' ? agreement.data! : null}
      onClose={onClose}
    />
  )
}

function BuilderHeader({ title, sub, onClose }: { title: string; sub: ReactNode; onClose: () => void }) {
  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-ink-200 px-6 py-4">
      <span className="grid size-8 place-items-center rounded-[9px] bg-green-50 text-green-700 ring-1 ring-green-200 ring-inset">
        <Icon name="agreement" className="size-4" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 id="agreement-builder-title" className="text-base font-semibold text-ink-900">
          {title}
        </h2>
        {sub ? <span className="text-xs text-ink-400">{sub}</span> : null}
      </div>
      <div className="ml-auto">
        <ModalCloseButton onClose={onClose} />
      </div>
    </header>
  )
}

function siteName(site: Pick<Site, 'name' | 'address' | 'ownerName'>): string {
  return site.name ?? site.ownerName ?? addressLine(site.address)
}

function siteSub(site: Pick<Site, 'name' | 'address' | 'number' | 'ownerName'>): string {
  return site.name || site.ownerName ? addressLine(site.address) : `Site ${site.number}`
}

/** "Oct 2026". */
function monthYear(day: string): string {
  return new Intl.DateTimeFormat('en-AU', { month: 'short', year: 'numeric' }).format(new Date(`${day}T12:00:00`))
}

function termLabel(term: Term): string {
  return `${monthYear(term.startsOn)} – ${monthYear(term.endsOn)}`
}

type StartChoice = 'job' | 'm1' | 'm2' | 'm3' | 'other'

function Builder({
  mode,
  settings,
  catalogue,
  agreement,
  onClose,
}: {
  mode: BuilderMode
  settings: BuilderSettings & { autoBook: boolean }
  catalogue: readonly ServiceType[]
  agreement: AgreementPage | null
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const job = mode.kind === 'new' ? (mode.job ?? null) : null
  const lockedClient =
    mode.kind === 'new' ? (mode.client ?? null) : { id: agreement!.client.id, name: agreement!.client.name }
  const steps: Step[] = mode.kind === 'extend' ? ['sites', 'services', 'review'] : ['client', 'sites', 'services', 'review']

  const [step, setStep] = useState<Step>(
    mode.kind === 'extend' && mode.siteIds?.length ? 'services' : steps[0]!,
  )
  const [client, setClient] = useState(lockedClient)
  const [name, setName] = useState(lockedClient && mode.kind === 'new' ? `${lockedClient.name} pest agreement` : '')
  const [nameEdited, setNameEdited] = useState(false)
  const [start, setStart] = useState<StartChoice>(job ? 'job' : 'm1')
  const [otherStart, setOtherStart] = useState(settings.today)
  const [billing, setBilling] = useState<AgreementBilling>('MONTHLY')
  const [autoRenew, setAutoRenew] = useState(true)
  const [picked, setPicked] = useState<string[] | null>(null)
  const [services, setServices] = useState<Record<string, DraftService[]>>(() =>
    job ? { [job.siteId]: servicesFromJob(job) } : {},
  )
  const [active, setActive] = useState<string | null>(
    job?.siteId ?? (mode.kind === 'extend' ? (mode.siteIds?.[0] ?? null) : null),
  )
  const [result, setResult] = useState<{ built: BuildResult; entries: { site: string; service: string }[] } | null>(null)

  const sites = useQuery({
    ...trpc.clients.sitesForClient.queryOptions({ clientId: client?.id ?? '', withChildren: true }),
    enabled: Boolean(client),
  })
  const allSites = sites.data ?? []

  const startOptions: { value: StartChoice; label: string; day: string }[] = [
    ...(job ? [{ value: 'job' as const, label: `With this job · ${dayLabel(job.dueOn, { year: false })}`, day: job.dueOn }] : []),
    ...([1, 2, 3] as const).map((months) => {
      const day = firstOfMonth(settings.today, months)
      return { value: `m${months}` as StartChoice, label: monthYear(day), day }
    }),
    { value: 'other', label: 'Other date', day: otherStart },
  ]
  const span: Term = agreement
    ? { startsOn: agreement.startsOn, endsOn: agreement.termEndsOn }
    : yearFrom(startOptions.find((option) => option.value === start)?.day ?? settings.today)
  const term: Term = { ...span, firstVisit: span.startsOn > settings.today ? span.startsOn : settings.today }

  const agreementSiteIds = agreement ? [...new Set(agreement.schedules.map((row) => row.siteId))] : []
  const defaultPicked = job
    ? [job.siteId]
    : mode.kind === 'extend'
      ? [...(mode.siteIds ?? agreementSiteIds)]
      : allSites.length <= 6
        ? allSites.map((site) => site.id)
        : []
  const pickedIds = picked ?? defaultPicked
  const selected = allSites.filter((site) => pickedIds.includes(site.id))
  const activeSite = selected.find((site) => site.id === active) ?? selected[0] ?? null
  const servicesAt = (siteId: string) => services[siteId] ?? []

  const totals = builderTotals(
    selected.map((site) => ({ id: site.id, name: siteName(site), services: servicesAt(site.id) })),
    term,
    settings,
    job,
  )

  const valid: Record<Step, boolean> = {
    client: Boolean(client) && name.trim().length > 0 && (start !== 'other' || /^\d{4}-\d{2}-\d{2}$/.test(otherStart)),
    sites: selected.length > 0,
    services: selected.length > 0 && selected.every((site) => servicesAt(site.id).length > 0),
    review: true,
  }
  const index = steps.indexOf(step)
  const reachable = (target: Step) => {
    const at = steps.indexOf(target)
    return at <= index || steps.slice(0, at).every((earlier) => valid[earlier])
  }

  const pickClient = (next: { id: string; name: string }) => {
    if (next.id === client?.id) return
    setClient(next)
    if (!nameEdited) setName(`${next.name} pest agreement`)
    setPicked(null)
    setServices({})
    setActive(null)
  }
  const toggleSite = (siteId: string) =>
    setPicked(pickedIds.includes(siteId) ? pickedIds.filter((id) => id !== siteId) : [...pickedIds, siteId])
  const updateServices = (siteId: string, change: (list: DraftService[]) => DraftService[]) =>
    setServices((current) => ({ ...current, [siteId]: change(current[siteId] ?? []) }))

  const build = useMutation(trpc.agreements.build.mutationOptions())
  const jobIncluded =
    job !== null &&
    selected.some((site) => site.id === job.siteId && servicesAt(site.id).some((service) => service.fromJob))

  const create = () => {
    const entries = selected.flatMap((site) =>
      servicesAt(site.id).map((service) => ({ site: siteName(site), service: service.name })),
    )
    build.mutate(
      {
        agreement: agreement
          ? { kind: 'existing', id: agreement.id }
          : {
              kind: 'new',
              clientId: client!.id,
              name: name.trim(),
              startsOn: term.startsOn,
              endsOn: term.endsOn,
              billing,
              autoRenew,
            },
        jobId: jobIncluded ? job!.id : null,
        sites: selected.map((site) => ({
          siteId: site.id,
          services: servicesAt(site.id).map((service) => ({
            serviceTypeId: service.serviceTypeId,
            intervalCount: service.intervalCount,
            intervalUnit: service.intervalUnit,
            firstDueOn: firstDueOf(service, term, job),
            priceIncTax: service.priceIncTax,
            ...(service.fromJob && jobIncluded ? { fromJob: true } : {}),
          })),
        })),
      },
      {
        onSuccess: (built) => {
          void queryClient.invalidateQueries(trpc.agreements.pathFilter())
          void queryClient.invalidateQueries(trpc.repeat.pathFilter())
          if (built.created.length) void queryClient.invalidateQueries(trpc.jobs.pathFilter())
          setResult({ built, entries })
        },
      },
    )
  }

  const title = agreement ? `Add to AG-${agreement.number}` : 'New service agreement'
  const sub = agreement ? agreement.name : job ? `From job ${job.number} · Draft` : 'Draft'

  if (result) {
    return (
      <>
        <BuilderHeader title={title} sub={sub} onClose={onClose} />
        <Created
          result={result}
          extending={Boolean(agreement)}
          settings={settings}
          visits={totals.visits}
          onOpen={() => {
            onClose()
            const created: AgreementCreated = {
              made: result.built.created.length,
              autoCreate: result.built.autoCreate,
              leadDays: settings.leadDays,
            }
            void navigate(`/agreements/${result.built.id}`, { state: { created } })
          }}
          onClose={onClose}
        />
      </>
    )
  }

  const next = () => {
    if (!valid[step]) return
    if (step === 'review') create()
    else setStep(steps[index + 1]!)
  }

  return (
    <>
      <BuilderHeader title={title} sub={sub} onClose={onClose} />
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_320px]">
        <nav
          aria-label="Steps"
          className="flex gap-1 overflow-x-auto border-b border-ink-200 bg-ink-50 px-3.5 py-2.5 lg:flex-col lg:border-r lg:border-b-0 lg:py-5"
        >
          {steps.map((entry, at) => {
            const current = entry === step
            const done = at < index
            const reach = reachable(entry)
            const subs: Record<Step, string> = {
              client: client ? client.name : 'Not chosen',
              sites: `${selected.length} selected`,
              services: `${totals.services} service${totals.services === 1 ? '' : 's'}`,
              review: agreement ? 'Check and add' : 'Check and create',
            }
            return (
              <button
                key={entry}
                type="button"
                disabled={!reach}
                aria-current={current ? 'step' : undefined}
                onClick={() => setStep(entry)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-[10px] p-2.5 text-left disabled:cursor-default disabled:opacity-55',
                  current && 'bg-white ring-1 ring-ink-200 ring-inset',
                )}
              >
                <span
                  className={cn(
                    'grid size-6 shrink-0 place-items-center rounded-full text-[11.5px] font-bold',
                    done
                      ? 'bg-green-600 text-white'
                      : current
                        ? 'bg-brand-800 text-white'
                        : 'bg-white text-ink-500 ring-[1.5px] ring-ink-300 ring-inset',
                  )}
                >
                  {done ? <Icon name="check" className="size-3" strokeWidth={3} /> : at + 1}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className={cn('text-[13px] font-semibold', current ? 'text-ink-900' : 'text-ink-600')}>
                    {STEP_LABEL[entry]}
                  </span>
                  <span className="truncate text-[11.5px] text-ink-400">{subs[entry]}</span>
                </span>
              </button>
            )
          })}
        </nav>

        <main className="flex min-h-0 min-w-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-7 py-6">
            {step === 'client' ? (
              <ClientStep
                locked={lockedClient !== null}
                client={client}
                onPick={pickClient}
                name={name}
                onName={(value) => {
                  setName(value)
                  setNameEdited(true)
                }}
                start={start}
                startOptions={startOptions}
                onStart={setStart}
                otherStart={otherStart}
                onOtherStart={setOtherStart}
                term={term}
                billing={billing}
                onBilling={setBilling}
                autoRenew={autoRenew}
                onAutoRenew={setAutoRenew}
              />
            ) : null}

            {step === 'sites' && client ? (
              <SitesStep
                clientId={client.id}
                clientName={client.name}
                loading={sites.isPending}
                sites={allSites}
                pickedIds={pickedIds}
                onToggle={toggleSite}
                onAll={(on) => setPicked(on ? allSites.map((site) => site.id) : [])}
                onCreated={(siteId) => setPicked([...pickedIds, siteId])}
                onAgreement={agreementSiteIds}
                jobSiteId={job?.siteId ?? null}
              />
            ) : null}

            {step === 'services' && activeSite ? (
              <ServicesStep
                selected={selected}
                activeSite={activeSite}
                onActive={setActive}
                servicesAt={servicesAt}
                update={updateServices}
                catalogue={catalogue}
                existing={agreement?.schedules.filter((row) => row.siteId === activeSite.id) ?? []}
                term={term}
                settings={settings}
                job={job}
              />
            ) : null}

            {step === 'review' ? (
              <ReviewStep
                heading={
                  agreement
                    ? `Adds ${totals.services} service${totals.services === 1 ? '' : 's'} to AG-${agreement.number} · ${totals.visits} visits this term across ${plural(selected.length, 'site')}.`
                    : `${name.trim()} · ${termLabel(term)}${autoRenew ? ', renews automatically' : ''}. ${totals.visits} visits across ${plural(selected.length, 'site')}.`
                }
                selected={selected}
                servicesAt={servicesAt}
                term={term}
                settings={settings}
                job={job}
                asideLabel={agreement ? 'This term' : 'A year'}
                onEdit={(siteId) => {
                  setActive(siteId)
                  setStep('services')
                }}
              />
            ) : null}

            {build.error ? <ErrorState message={errorMessage(build.error)} /> : null}
          </div>

          <footer className="flex shrink-0 items-center gap-3 border-t border-ink-200 px-7 py-3.5">
            {index > 0 ? (
              <Button onClick={() => setStep(steps[index - 1]!)}>Back</Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            )}
            <span className="ml-auto text-[12.5px] text-ink-400" aria-live="polite">
              {valid[step] ? '' : BLOCKER[step]}
            </span>
            <Button variant="primary" disabled={!valid[step]} loading={build.isPending} onClick={next}>
              {step === 'review' ? (agreement ? 'Add to agreement' : 'Create agreement') : 'Continue'}
            </Button>
          </footer>
        </main>

        <Summary
          clientName={agreement ? `AG-${agreement.number} · ${agreement.name}` : client ? client.name : null}
          termLine={
            agreement
              ? `${dayLabel(agreement.startsOn)} – ${dayLabel(agreement.termEndsOn)}${agreement.autoRenew ? ' · renews' : ''}`
              : `${termLabel(term)}${autoRenew ? ' · renews' : ''}`
          }
          totals={totals}
          billing={agreement ? agreement.billing : billing}
          settings={settings}
          adding={Boolean(agreement)}
        />
      </div>
    </>
  )
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function StepHeading({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-end gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="text-xl font-semibold tracking-[-0.01em] text-ink-900">{title}</h3>
        {children ? <p className="text-[13.5px] text-ink-500">{children}</p> : null}
      </div>
      {action ? <div className="ml-auto">{action}</div> : null}
    </div>
  )
}

/** A row of choices where one is on: the mockup's segmented control, as radios. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap self-start rounded-[9px] bg-ink-200 p-[3px]">
      {options.map((option) => {
        const on = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold whitespace-nowrap',
              on ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-800',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function ClientStep({
  locked,
  client,
  onPick,
  name,
  onName,
  start,
  startOptions,
  onStart,
  otherStart,
  onOtherStart,
  term,
  billing,
  onBilling,
  autoRenew,
  onAutoRenew,
}: {
  locked: boolean
  client: { id: string; name: string } | null
  onPick: (client: { id: string; name: string }) => void
  name: string
  onName: (name: string) => void
  start: StartChoice
  startOptions: { value: StartChoice; label: string }[]
  onStart: (start: StartChoice) => void
  otherStart: string
  onOtherStart: (day: string) => void
  term: Term
  billing: AgreementBilling
  onBilling: (billing: AgreementBilling) => void
  autoRenew: boolean
  onAutoRenew: (on: boolean) => void
}) {
  const trpc = useTRPC()
  const [query, setQuery] = useState('')
  const clients = useQuery({
    ...trpc.clients.list.queryOptions({ q: query.trim() || undefined, perPage: 12, sort: 'name', dir: 'asc' }),
    enabled: !locked,
    placeholderData: keepPreviousData,
  })

  return (
    <>
      <StepHeading title="Who is the agreement with?">
        Pick the client, then set the term and how they’re billed.
      </StepHeading>

      {locked && client ? (
        <div role="radiogroup" aria-label="Client" className="grid gap-2 sm:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
          <ClientCard name={client.name} sub="" on onClick={() => undefined} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Input
            aria-label="Search clients"
            placeholder="Search clients"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div role="radiogroup" aria-label="Client" className="grid gap-2 sm:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
            {(clients.data?.items ?? []).map((row) => {
              const contact = row.contacts[0]
              return (
                <ClientCard
                  key={row.id}
                  name={row.name}
                  sub={contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') : ''}
                  sites={row._count.sites}
                  on={row.id === client?.id}
                  onClick={() => onPick({ id: row.id, name: row.name })}
                />
              )
            })}
          </div>
          {clients.isPending ? (
            <p className="flex items-center gap-1.5 text-xs text-ink-400">
              <Spinner className="size-3" /> Loading clients…
            </p>
          ) : (clients.data?.items.length ?? 0) === 0 ? (
            <p className="text-[13px] text-ink-500">No client matches that.</p>
          ) : null}
        </div>
      )}

      {client ? (
        <div className="flex flex-col gap-5 pt-1">
          <div className="max-w-[420px]">
            <Field label="Agreement name" htmlFor="builder-name">
              <Input id="builder-name" value={name} onChange={(event) => onName(event.target.value)} />
            </Field>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-ink-700">Starts</span>
              <Segmented label="Starts" value={start} options={startOptions} onChange={onStart} />
              {start === 'other' ? (
                <Input
                  type="date"
                  aria-label="Start date"
                  value={otherStart}
                  onChange={(event) => onOtherStart(event.target.value)}
                  className="mt-1 w-48"
                />
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[12.5px] font-semibold text-ink-700">Term</span>
              <span className="flex h-[38px] items-center text-[13.5px] text-ink-900">{`12 months · ${termLabel(term)}`}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-ink-700">Billing</span>
            <Segmented
              label="Billing"
              value={billing}
              options={(['MONTHLY', 'PER_VISIT', 'UPFRONT'] as const).map((value) => ({ value, label: BILLING_LABEL[value] }))}
              onChange={onBilling}
            />
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoRenew}
            onClick={() => onAutoRenew(!autoRenew)}
            className="flex max-w-[420px] items-center gap-3 text-left"
          >
            <SwitchTrack checked={autoRenew} />
            <span className="flex flex-col gap-0.5">
              <span className="text-[13.5px] font-semibold text-ink-900">Renew automatically</span>
              <span className="text-[12.5px] text-ink-500">Rolls into a new 12-month term with the same services</span>
            </span>
          </button>
          <p className="text-[12px] text-ink-400">
            Billing and renewal are recorded on the agreement. Nothing invoices or renews from them yet.
          </p>
        </div>
      ) : null}
    </>
  )
}

function ClientCard({
  name,
  sub,
  sites,
  on,
  onClick,
}: {
  name: string
  sub: string
  sites?: number
  on: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      aria-label={name}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-[10px] px-3.5 py-3 text-left',
        on ? 'bg-green-50/60 ring-[1.5px] ring-brand-600 ring-inset' : 'bg-white ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'size-[18px] shrink-0 rounded-full bg-white',
          on ? 'border-[5px] border-brand-600' : 'border-[1.5px] border-ink-300',
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[13.5px] font-semibold text-ink-900">{name}</span>
        {sub ? <span className="truncate text-xs text-ink-500">{sub}</span> : null}
      </span>
      {sites !== undefined ? (
        <span className="ml-auto text-xs whitespace-nowrap text-ink-400">{plural(sites, 'site')}</span>
      ) : null}
    </button>
  )
}

function SitesStep({
  clientId,
  clientName,
  loading,
  sites,
  pickedIds,
  onToggle,
  onAll,
  onCreated,
  onAgreement,
  jobSiteId,
}: {
  clientId: string
  clientName: string
  loading: boolean
  sites: readonly Site[]
  pickedIds: readonly string[]
  onToggle: (siteId: string) => void
  onAll: (on: boolean) => void
  onCreated: (siteId: string) => void
  onAgreement: readonly string[]
  jobSiteId: string | null
}) {
  const [adding, setAdding] = useState(false)
  const [created, setCreated] = useState<string[]>([])
  const allOn = sites.length > 0 && sites.every((site) => pickedIds.includes(site.id))

  return (
    <>
      <StepHeading
        title="Which sites are covered?"
        action={
          sites.length > 1 ? (
            <button type="button" className="text-[12.5px] font-semibold text-brand-700 hover:underline" onClick={() => onAll(!allOn)}>
              {allOn ? 'Select none' : 'Select all'}
            </button>
          ) : null
        }
      >
        {`${clientName} has ${plural(sites.length, 'site')} on file.`}
      </StepHeading>

      <div className="flex flex-col rounded-xl ring-1 ring-ink-200 ring-inset">
        {loading ? (
          <p className="flex items-center gap-1.5 px-4 py-3 text-xs text-ink-400">
            <Spinner className="size-3" /> Loading properties…
          </p>
        ) : null}
        {sites.map((site) => {
          const on = pickedIds.includes(site.id)
          const tag = created.includes(site.id)
            ? 'New'
            : site.id === jobSiteId
              ? 'This job'
              : onAgreement.includes(site.id)
                ? 'On the agreement'
                : ''
          return (
            <button
              key={site.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={siteName(site)}
              onClick={() => onToggle(site.id)}
              className="flex items-center gap-3 border-b border-ink-100 px-4 py-3 text-left hover:bg-ink-50"
            >
              {on ? (
                <span className="grid size-[18px] shrink-0 place-items-center rounded-[5px] bg-brand-600 text-white">
                  <Icon name="check" className="size-3" strokeWidth={3} />
                </span>
              ) : (
                <span className="size-[18px] shrink-0 rounded-[5px] border-[1.5px] border-ink-300 bg-white" />
              )}
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[13.5px] font-semibold text-ink-900">{siteName(site)}</span>
                <span className="truncate text-[12.5px] text-ink-500">{siteSub(site)}</span>
              </span>
              {tag ? <span className="ml-auto text-xs whitespace-nowrap text-ink-400">{tag}</span> : null}
            </button>
          )
        })}
        {adding ? (
          <div className="p-3">
            <NewSitePanel
              clientId={clientId}
              onCancel={() => setAdding(false)}
              onCreated={(siteId) => {
                setAdding(false)
                setCreated((current) => [...current, siteId])
                onCreated(siteId)
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 px-4 py-3 text-[13px] font-semibold text-brand-700 hover:bg-ink-50"
          >
            <Icon name="plus" className="size-4" />
            Add a site
          </button>
        )}
      </div>
    </>
  )
}

function frequencyValue(interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit }): string {
  return `${interval.intervalCount}-${interval.intervalUnit}`
}

function ServicesStep({
  selected,
  activeSite,
  onActive,
  servicesAt,
  update,
  catalogue,
  existing,
  term,
  settings,
  job,
}: {
  selected: readonly Site[]
  activeSite: Site
  onActive: (siteId: string) => void
  servicesAt: (siteId: string) => DraftService[]
  update: (siteId: string, change: (list: DraftService[]) => DraftService[]) => void
  catalogue: readonly ServiceType[]
  existing: AgreementPage['schedules']
  term: Term
  settings: BuilderSettings
  job: BuilderJob | null
}) {
  const [filter, setFilter] = useState('')
  const items = servicesAt(activeSite.id)
  const others = selected.filter((site) => site.id !== activeSite.id)
  const taken = new Set([
    ...items.map((item) => item.serviceTypeId),
    ...existing.flatMap((row) => row.services.map((service) => service.serviceTypeId)),
  ])
  const needle = filter.trim().toLowerCase()
  const addable = catalogue
    .filter((type) => !taken.has(type.id))
    .filter((type) => !needle || type.name.toLowerCase().includes(needle))

  const copyToOthers = () => {
    for (const site of others) {
      update(site.id, (list) => {
        const kept = list.filter((service) => service.fromJob)
        const copies = items
          .filter((service) => !kept.some((keep) => keep.serviceTypeId === service.serviceTypeId))
          .map((service) => ({
            ...service,
            key: `${service.serviceTypeId}-${Math.random().toString(36).slice(2, 8)}`,
            fromJob: false,
            firstDueOn: service.fromJob ? null : service.firstDueOn,
          }))
        return [...kept, ...copies]
      })
    }
  }

  const rows = items.map((service) => ({
    id: service.key,
    name: service.name,
    sub: intervalLabel(service),
    slots: draftSlots(service, term, settings, job),
  }))

  return (
    <>
      <StepHeading title="What do you do at each site?">Each service runs on its own schedule.</StepHeading>

      {selected.length > 1 ? (
        <div role="tablist" aria-label="Sites" className="flex flex-wrap gap-1.5">
          {selected.map((site) => {
            const on = site.id === activeSite.id
            const count = servicesAt(site.id).length
            return (
              <button
                key={site.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => onActive(site.id)}
                className={cn(
                  'inline-flex h-[34px] items-center gap-2 rounded-[9px] px-3 text-[13px] font-semibold',
                  on ? 'bg-brand-800 text-white' : 'bg-white text-ink-700 ring-1 ring-ink-300 ring-inset',
                )}
              >
                {siteName(site)}
                <span
                  className={cn(
                    'min-w-[18px] rounded-full px-1.5 py-px text-center text-[11px] font-bold',
                    count
                      ? on
                        ? 'bg-white/20 text-white'
                        : 'bg-green-50 text-green-800'
                      : on
                        ? 'bg-red-500 text-white'
                        : 'bg-red-50 text-red-700',
                  )}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}

      <div className="flex flex-col gap-3.5 rounded-xl bg-ink-50 p-[18px] ring-1 ring-ink-200 ring-inset">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[15px] font-semibold text-ink-900">{siteName(activeSite)}</span>
          <span className="text-[12.5px] text-ink-500">{siteSub(activeSite)}</span>
          {others.length > 0 && items.length > 0 ? (
            <button
              type="button"
              onClick={copyToOthers}
              className="ml-auto inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-700 hover:underline"
            >
              <Icon name="copy" className="size-3.5" />
              {`Copy to the other ${plural(others.length, 'site')}`}
            </button>
          ) : null}
        </div>

        {existing.length > 0 ? (
          <p className="text-[12.5px] text-ink-500">
            {`Already on the agreement here: ${existing.map((row) => row.services.map((service) => service.name).join(', ')).join('; ')}.`}
          </p>
        ) : null}

        {items.length > 0 ? (
          <div className="flex flex-col gap-2">
            {items.map((service) => (
              <ServiceCard
                key={service.key}
                service={service}
                term={term}
                settings={settings}
                job={job}
                onChange={(patch) =>
                  update(activeSite.id, (list) => list.map((entry) => (entry.key === service.key ? { ...entry, ...patch } : entry)))
                }
                onRemove={() => update(activeSite.id, (list) => list.filter((entry) => entry.key !== service.key))}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-[10px] border-[1.5px] border-dashed border-ink-300 p-4 text-center text-[13px] text-ink-500">
            No services at this site yet. Add one below.
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold tracking-[0.06em] text-ink-400 uppercase">Add a service</span>
            {catalogue.length > 12 ? (
              <Input
                aria-label="Filter services"
                placeholder="Filter services"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="ml-auto h-8 max-w-[220px] text-[13px]"
              />
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {addable.map((type) => {
              const interval =
                job && job.interval && job.services.some((service) => service.serviceTypeId === type.id)
                  ? job.interval
                  : DEFAULT_FREQUENCY
              return (
                <button
                  key={type.id}
                  type="button"
                  aria-label={`Add ${type.name}`}
                  onClick={() => update(activeSite.id, (list) => [...list, newDraftService(type, interval)])}
                  className="inline-flex h-[34px] items-center gap-2 rounded-[9px] bg-white px-3 text-[13px] font-semibold text-ink-700 ring-1 ring-ink-300 ring-inset hover:ring-[1.5px] hover:ring-brand-600"
                >
                  <Icon name="plus" className="size-3.5 text-brand-700" strokeWidth={2.25} />
                  {type.name}
                  <span className="font-medium text-ink-400">
                    {`${shortFrequency(interval)}${type.defaultPriceIncTax ? ` · ${money(type.defaultPriceIncTax)}` : ''}`}
                  </span>
                </button>
              )
            })}
            {addable.length === 0 ? (
              <span className="text-[12.5px] text-ink-400">
                {needle ? 'No service matches that.' : 'Every service is already here.'}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-bold tracking-[0.06em] text-ink-400 uppercase">{`Visits at ${siteName(activeSite)}`}</span>
          <ServiceTracks
            label={`Visits at ${siteName(activeSite)}`}
            months={termMonths(term.startsOn, term.endsOn)}
            today={settings.today}
            groups={[{ key: activeSite.id, rows }]}
            currentJobId={job?.id ?? null}
            showKey={false}
          />
        </div>
      ) : null}
    </>
  )
}

function ServiceCard({
  service,
  term,
  settings,
  job,
  onChange,
  onRemove,
}: {
  service: DraftService
  term: Term
  settings: BuilderSettings
  job: BuilderJob | null
  onChange: (patch: Partial<DraftService>) => void
  onRemove: () => void
}) {
  const slots = draftSlots(service, term, settings, job)
  const id = `builder-${service.key}`
  const frequencies = FREQUENCIES.some((option) => frequencyValue(option) === frequencyValue(service))
    ? FREQUENCIES
    : [...FREQUENCIES, { intervalCount: service.intervalCount, intervalUnit: service.intervalUnit }]

  return (
    <section
      aria-label={service.name}
      className="flex flex-col gap-2.5 rounded-[10px] bg-white px-3.5 py-3 ring-1 ring-ink-200 ring-inset"
    >
      <div className="flex items-center gap-2.5">
        <span className="text-[13.5px] font-semibold text-ink-900">{service.name}</span>
        {service.fromJob && job ? (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
            {`Starts with ${job.number}`}
          </span>
        ) : null}
        <span className="ml-auto flex items-baseline gap-1.5 text-[12.5px]">
          <span className="text-[11.5px] text-ink-400">{plural(slots.length, 'visit')}</span>
          <span className="font-semibold text-ink-900 tabular-nums">{money(slots.length * (service.priceIncTax ?? 0))}</span>
        </span>
        <button
          type="button"
          aria-label={`Remove ${service.name}`}
          onClick={onRemove}
          className="grid size-7 place-items-center rounded-[7px] text-ink-400 hover:bg-red-50 hover:text-red-700"
        >
          <Icon name="close" className="size-3.5" />
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-400" htmlFor={`${id}-often`}>
          How often
          <Select
            id={`${id}-often`}
            value={frequencyValue(service)}
            onChange={(event) => {
              const [count, unit] = event.target.value.split('-') as [string, RepeatIntervalUnit]
              onChange({ intervalCount: Number(count), intervalUnit: unit })
            }}
          >
            {frequencies.map((option) => (
              <option key={frequencyValue(option)} value={frequencyValue(option)}>
                {intervalLabel(option)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-400" htmlFor={`${id}-first`}>
          First visit
          {service.fromJob && job ? (
            <span id={`${id}-first`} className="flex h-[var(--control-md)] items-center text-[13px] font-medium text-ink-900">
              {`This job · ${dayLabel(job.dueOn, { year: false })}`}
            </span>
          ) : (
            <Input
              id={`${id}-first`}
              type="date"
              min={term.startsOn}
              max={term.endsOn}
              value={firstDueOf(service, term, job)}
              onChange={(event) => onChange({ firstDueOn: event.target.value || null })}
            />
          )}
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-400" htmlFor={`${id}-price`}>
          Per visit
          <PriceInput
            id={`${id}-price`}
            value={service.priceIncTax}
            onChange={(cents) => onChange({ priceIncTax: cents })}
            placeholder="$0.00"
          />
        </label>
      </div>
    </section>
  )
}

function ReviewStep({
  heading,
  selected,
  servicesAt,
  term,
  settings,
  job,
  asideLabel,
  onEdit,
}: {
  heading: string
  selected: readonly Site[]
  servicesAt: (siteId: string) => DraftService[]
  term: Term
  settings: BuilderSettings
  job: BuilderJob | null
  asideLabel: string
  onEdit: (siteId: string) => void
}) {
  const groups: TrackGroup[] = selected.map((site) => ({
    key: site.id,
    heading: (
      <span className="flex items-baseline gap-2.5">
        <span className="text-[13.5px] font-semibold text-ink-900">{siteName(site)}</span>
        <span className="truncate text-xs text-ink-500">{siteSub(site)}</span>
        <button
          type="button"
          aria-label={`Edit ${siteName(site)}`}
          onClick={() => onEdit(site.id)}
          className="ml-auto text-xs font-semibold text-brand-700 hover:underline"
        >
          Edit
        </button>
      </span>
    ),
    rows: servicesAt(site.id).map((service) => {
      const slots = draftSlots(service, term, settings, job)
      return {
        id: `${site.id}-${service.key}`,
        name: service.name,
        sub: [intervalLabel(service), service.priceIncTax ? money(service.priceIncTax) : null].filter(Boolean).join(' · '),
        slots,
        aside: money(slots.length * (service.priceIncTax ?? 0)),
      }
    }),
  }))

  return (
    <>
      <StepHeading title="Check and create">{heading}</StepHeading>
      <ServiceTracks
        label="Sites and services"
        months={termMonths(term.startsOn, term.endsOn)}
        today={settings.today}
        groups={groups}
        currentJobId={job?.id ?? null}
        showAside
        asideLabel={asideLabel}
        showKey={false}
      />
    </>
  )
}

function Summary({
  clientName,
  termLine,
  totals,
  billing,
  settings,
  adding,
}: {
  clientName: string | null
  termLine: string
  totals: ReturnType<typeof builderTotals>
  billing: AgreementBilling
  settings: BuilderSettings
  adding: boolean
}) {
  const lead = leadLabel(settings.leadDays)
  const first = totals.jobs.slice(0, 4)
  return (
    <aside
      aria-label="Summary"
      className="flex flex-col gap-4 overflow-y-auto border-t border-ink-200 bg-ink-50 p-[22px] lg:border-t-0 lg:border-l"
    >
      <span className="text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase">Summary</span>
      <div className="flex flex-col gap-0.5">
        <span className="text-[15px] font-semibold text-ink-900">{clientName ?? 'No client yet'}</span>
        <span className="text-[12.5px] text-ink-500">{clientName ? termLine : 'Pick a client to start'}</span>
      </div>
      <dl className="grid grid-cols-2 gap-2">
        <Stat term="Sites">{totals.sites}</Stat>
        <Stat term={adding ? 'New services' : 'Services'}>{totals.services}</Stat>
        <Stat term="Visits in term">{totals.visits}</Stat>
        <Stat term={adding ? 'This term' : 'A year'}>{money(totals.value)}</Stat>
      </dl>
      <div className="flex items-center gap-2 rounded-[10px] bg-white px-3 py-2.5 text-[12.5px] text-ink-700 ring-1 ring-ink-200 ring-inset">
        <Icon name="card" className="size-4 text-ink-500" />
        {clientName ? billingLine(billing, totals.value) : 'Billing not set'}
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase">First jobs</span>
        {first.length > 0 ? (
          <ul aria-label="First jobs" className="flex flex-col rounded-[10px] bg-white ring-1 ring-ink-200 ring-inset">
            {first.map((entry) => (
              <li key={entry.key} className="flex flex-col gap-0.5 border-b border-ink-100 px-3 py-2.5 last:border-b-0">
                <span className="flex items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-ink-900">{dayLabel(entry.dueOn, { year: false })}</span>
                  <span className="truncate text-[12.5px] text-ink-700">{entry.service}</span>
                </span>
                <span className="flex gap-2 text-[11.5px]">
                  <span className="truncate text-ink-500">{entry.site}</span>
                  <span className={cn('ml-auto font-semibold whitespace-nowrap', entry.now ? 'text-blue-700' : 'text-ink-400')}>
                    {whenLabel(entry, settings)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-[12.5px] leading-normal text-ink-400">Add services to see when the first jobs land.</span>
        )}
        <span className="text-[11.5px] leading-normal text-ink-400">
          {settings.autoCreate
            ? `Jobs are created ${lead} before they’re due. Change this in Settings · Scheduling.`
            : `Automatic creation is off, so each job is made by hand from its Schedule tab, from ${lead} before it’s due. Settings · Scheduling.`}
        </span>
      </div>
    </aside>
  )
}

function whenLabel(entry: { now: boolean; createOn: string }, settings: BuilderSettings): string {
  if (settings.autoCreate) return entry.now ? 'Created right away' : `Created ${dayLabel(entry.createOn, { year: false })}`
  return entry.createOn <= settings.today ? 'Make it now' : `Make it from ${dayLabel(entry.createOn, { year: false })}`
}

function Stat({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-0.5 rounded-[10px] bg-white px-3 py-2.5 ring-1 ring-ink-200 ring-inset">
      <dt className="text-[11.5px] text-ink-500">{term}</dt>
      <dd className="text-lg font-semibold text-ink-900 tabular-nums">{children}</dd>
    </div>
  )
}

function Created({
  result,
  extending,
  settings,
  visits,
  onOpen,
  onClose,
}: {
  result: { built: BuildResult; entries: { site: string; service: string }[] }
  extending: boolean
  settings: BuilderSettings
  visits: number
  onOpen: () => void
  onClose: () => void
}) {
  const { built, entries } = result
  const lead = leadLabel(settings.leadDays)
  const made = built.created.length
  // A job can carry several visits at one site, so jobs and visits differ.
  const covered = built.created.reduce((sum, row) => sum + row.scheduleIds.length, 0)
  const line = !built.autoCreate
    ? `Automatic creation is off, so no jobs were made. Each visit’s job is made by hand from its Schedule tab, from ${lead} before it’s due.`
    : made
      ? `${made} job${made === 1 ? ' was' : 's were'} created and put on the schedule. The other ${Math.max(0, visits - covered)} will be created ${lead} before they’re due.`
      : `No visits fall in the next ${lead}. Each visit’s job will be created ${lead} before it’s due.`

  return (
    <div className="grid flex-1 place-items-center overflow-y-auto bg-ink-50 px-6 py-12">
      <div className="flex w-full max-w-[560px] flex-col gap-[18px]">
        <span className="grid size-12 place-items-center rounded-full bg-green-600 text-white ring-[6px] ring-green-100">
          <Icon name="check" className="size-5" strokeWidth={2.5} />
        </span>
        <div className="flex flex-col gap-1.5">
          <h3 className="text-2xl font-semibold tracking-[-0.01em] text-ink-900">
            {extending ? `Added to AG-${built.number}` : `AG-${built.number} is active`}
          </h3>
          <p className="text-sm leading-relaxed text-pretty text-ink-600">{line}</p>
        </div>
        {made > 0 ? (
          <ul aria-label="Jobs created" className="flex flex-col rounded-xl bg-white ring-1 ring-ink-200 ring-inset">
            {built.created.map((row) => {
              const entry = entries[built.scheduleIds.indexOf(row.scheduleId)]
              const services = row.scheduleIds
                .map((id) => entries[built.scheduleIds.indexOf(id)]?.service)
                .filter((name): name is string => Boolean(name))
              return (
                <li
                  key={row.jobId}
                  className="grid grid-cols-[90px_minmax(0,1fr)_auto] items-center gap-3 border-b border-ink-100 px-4 py-2.5 text-[13px] last:border-b-0"
                >
                  <span className="font-semibold text-ink-900">{dayLabel(row.dueOn, { year: false })}</span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-ink-900">
                      {services.length > 0 ? services.join(', ') : 'Service'}
                    </span>
                    <span className="truncate text-xs text-ink-500">{entry?.site ?? ''}</span>
                  </span>
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11.5px] font-semibold text-blue-700">On the schedule</span>
                </li>
              )
            })}
          </ul>
        ) : null}
        <div className="flex gap-2">
          {extending ? (
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="primary" onClick={onOpen}>
                Open agreement
              </Button>
              <Button onClick={onClose}>Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
